<template>
    <page-loading v-if="loading"></page-loading>
    <vx-page-layout v-else :layout="props.phoneNumberId ? 'sidebar' : 'create'">
        <template v-slot:title>
            <span v-if="props.phoneNumberId">{{ formatPhoneNumber(data.phone_number) }}</span>
            <span v-else class="inline-flex items-center gap-3">
                <font-awesome-icon widthAuto :icon="faPhone" class="text-base"></font-awesome-icon>
                <span>New Phone Number</span>
            </span>
        </template>

        <template v-if="!props.phoneNumberId" v-slot:description>
            Find a new phone number to use with Voxie.
        </template>

        <template v-if="props.phoneNumberId" v-slot:subtitle>
            <a :href="`/settings/teams/${teamId}#/phone-numbers`"
                class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1"
                    :icon="faArrowLeft"></font-awesome-icon>
                All Phone Numbers
            </a>
        </template>

        <template v-if="props.phoneNumberId" v-slot:sidebar>
            <page-sidebar
                appearance="nested"
                :links="[
                    { href: `/settings/teams/${teamId}/phone-numbers/${props.phoneNumberId}`, label: 'Details' },
                ]"></page-sidebar>
        </template>
        <form-wrapper
            :sections="[
                { id: 'search', label: 'Search' },
                { id: 'sending', label: 'Sending' },
                FEATURES.contact_groups ? { id: 'group', label: 'Group' } : undefined,
                { id: 'voice', label: 'Voice' },
                isImpersonatingOrPlatformAdmin ? { id: 'request', label: 'Request' } : undefined,
            ].filter(section => Boolean(section))"
            :hideNav="Boolean(success || props.phoneNumberId)"
        >

            <vx-alert v-if="success"
                color="success"
                :closeable="false"
                shadow>
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </template>
                <div class="flex items-center justify-between">
                    <span><strong>Phone Number</strong> {{ props.phoneNumberId ? 'updated' : 'created' }} successfully</span>
                    <vx-button :href="`/settings/teams/${teamId}#/phone-numbers`"
                        size="sm"
                        color="success">Back to
                        Phone Numbers <font-awesome-icon widthAuto
                            :icon="faSquareArrowUpLeft"></font-awesome-icon></vx-button>
                </div>
            </vx-alert>


            <team-phone-number-form-search v-if="!props.phoneNumberId" id="search"
                    v-model="selectedNumber"></team-phone-number-form-search>

            <team-phone-number-form-sending id="sending"
                v-model="data.use_for_sending"
                :disabled="!isImpersonatingOrPlatformAdmin"></team-phone-number-form-sending>

            <team-phone-number-form-group v-if="FEATURES.contact_groups" id="group"
                v-model="data.group_id"
                :disabled="!isImpersonatingOrPlatformAdmin"></team-phone-number-form-group>

            <template v-if="!isShortCode">
                <team-phone-number-form-voice v-if="isImpersonatingOrPlatformAdmin" id="voice"
                        v-model="data"></team-phone-number-form-voice>
                <team-phone-number-form-voice-read-only v-else id="voice"
                    :phoneNumber="data"></team-phone-number-form-voice-read-only>
            </template>

            <team-phone-number-form-requested-by v-if="isImpersonatingOrPlatformAdmin && !data.is_hosted && !isShortCode" id="request"
                v-model="data.requested_by"></team-phone-number-form-requested-by>

            <vx-sticky-footer v-if="isImpersonatingOrPlatformAdmin">
                <vx-button :href="`/settings/teams/${teamId}#/phone-numbers`"
                    class="w-20"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="submit()"
                    :loading="submitting"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary">
                    {{ props.phoneNumberId ? 'Update' : 'Create' }}
                </vx-button>
            </vx-sticky-footer>

        </form-wrapper>
    </vx-page-layout>
</template>

<script setup>

import {
    VxAlert,
    VxButton,
    VxPageLayout,
    VxStickyFooter,
} from '@voxie/frontend-components';
import { computed, inject, onMounted, ref , watch } from 'vue';
import { faArrowLeft, faCircleCheck, faPhone, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TeamPhoneNumberFormSearch from './TeamPhoneNumberFormSearch.vue';
import TeamPhoneNumberFormSending from './TeamPhoneNumberFormSending.vue';
import TeamPhoneNumberFormGroup from './TeamPhoneNumberFormGroup.vue';
import TeamPhoneNumberFormVoice from './TeamPhoneNumberFormVoice.vue';
import TeamPhoneNumberFormVoiceReadOnly from './TeamPhoneNumberFormVoiceReadOnly.vue';
import TeamPhoneNumberFormRequestedBy from './TeamPhoneNumberFormRequestedBy.vue';
import { adminPhoneNumbersClient , phoneNumbersClient } from '~/services';
import useVuelidate from '@vuelidate/core';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { formatPhoneNumber } from '~/components/filters';
import PageLoading from '~/components/general/PageLoading.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { validateRules } from '~/utils/validation';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';

const FEATURES = inject('FEATURES');

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;

const props = defineProps({
    phoneNumberId: {
        type: Number,
        required: false,
    }
})

const selectedNumber = ref();

const defaultData = () => ({
    phone_number: '',
    use_for_sending: false,
    group_id: FEATURES.contact_groups ? null : undefined,
    voice_message_text: '',
    forward_voice_to: '',
    forward_voice_send_digits: '',
    requested_by: '',
});

const data = ref(defaultData());

const isShortCode = computed(() => data.value.number_type === 'shortcode');

watch(selectedNumber, () => {
    data.value.phone_number = selectedNumber.value?.phone_number;
}, {
    deep: true,
})

const success = ref(false);
const loading = ref(false);
const submitting = ref(false);
const { catchError } = useFormRequestHandler();
const validate = useVuelidate();

onMounted(async () => {
    if (props.phoneNumberId) {
        try {
            loading.value = true;
            const response = await phoneNumbersClient.showPhoneNumber(teamId, props.phoneNumberId);
            data.value = { ...response.data, requested_by: '' };
        } finally {
            loading.value = false;
        }
    }
})

// The API ignores the voice settings of a hosted number or a short code, and a legacy stored destination would fail validation.
const VOICE_FIELDS = ['forward_voice_to', 'forward_voice_send_digits', 'voice_message_text'];

const submit = async () => {
    success.value = false;

    // when creating enforce phone number selection
    if (!props.phoneNumberId && (!selectedNumber.value?.phone_number || !selectedNumber.value?.iso_country)) {
        toasted.error("Search a phone number and select it before saving", {
            sticky: true,
        });
        return;
    }

    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        let phoneNumberId = props.phoneNumberId;

        if (!phoneNumberId) {
            // purchase number when not created yet
            const purchaseResponse = await adminPhoneNumbersClient.create(
                teamId,
                { vendor: selectedNumber.value.vendor, phone_number: selectedNumber.value.phone_number, country: selectedNumber.value.iso_country, requested_by: data.value.requested_by }
            );

            phoneNumberId = purchaseResponse.data.id;
        }

        const payload = data.value.is_hosted || isShortCode.value
            ? Object.fromEntries(Object.entries(data.value).filter(([key]) => !VOICE_FIELDS.includes(key)))
            : data.value;

        await adminPhoneNumbersClient.update(teamId, phoneNumberId, payload);

        success.value = true;

        validate.value.$reset();

        if (!props.phoneNumberId) {
            selectedNumber.value = {};
            data.value = defaultData();
        } else {
            data.value.requested_by = '';
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
       catchError(e);
    } finally {
        submitting.value = false;
    }
}
</script>
